import { apiPaginated, apiRequest } from "@/api/client";
import { endpoints } from "@/api/endpoints";
import type {
  Article,
  CompetitionHistoryEdition,
  Fixture,
  Gallery,
  LeagueTable,
  MatchDetail,
  MatchLiveState,
  Player,
  Sponsor,
  StaffMember,
  Stream,
  Team,
} from "@/types/domain";
import type { ListParams } from "@/api/types";
import type { ApiData } from "@/api/types";

export interface Video {
  publicId: string;
  title: string;
  duration: number;
  category: string;
  publishedAt: string;
  viewCount: number;
  thumbnailUrl: string;
  matchPublicId: string | null;
  playerPublicId: string | null;
  hlsUrl: string;
}

export const publicService = {
  home: () => apiRequest<ApiData>(endpoints.public.home),
  settings: () => apiRequest<ApiData>(endpoints.public.settings),
  navigation: () => apiRequest<ApiData>(endpoints.public.navigation),
  teams: () => apiRequest<Team[]>(endpoints.public.teams),
  team: (id: string) => apiRequest<Team>(endpoints.public.team(id)),
  players: (params?: ListParams) =>
    apiPaginated<Player>(endpoints.public.players, { query: params }),
  player: (id: string) => apiRequest<Player>(endpoints.public.player(id)),
  staff: () => apiRequest<StaffMember[]>(endpoints.public.staff),
  fixtures: (params?: ListParams) =>
    apiPaginated<Fixture>(endpoints.public.fixtures, { query: params }),
  fixture: (id: string) => apiRequest<Fixture>(endpoints.public.fixture(id)),
  results: (params?: ListParams) =>
    apiPaginated<Fixture>(endpoints.public.results, { query: params }),
  match: (id: string) => apiRequest<MatchDetail>(endpoints.public.match(id)),
  matchLiveState: (id: string) => apiRequest<MatchLiveState>(endpoints.public.matchLiveState(id)),
  liveMatch: () => apiRequest<Fixture | null>(endpoints.public.liveMatch),
  leagueTables: () => apiRequest<LeagueTable[]>(endpoints.public.leagueTables),
  competitionHistory: () =>
    apiRequest<CompetitionHistoryEdition[]>(endpoints.public.competitionHistory),
  competitionHistorySeasons: (slug: string) =>
    apiRequest<CompetitionHistoryEdition[]>(endpoints.public.competitionHistorySeasons(slug)),
  historicalStandings: (slug: string, season: string) =>
    apiRequest<LeagueTable>(endpoints.public.competitionHistoryStandings(slug, season)),
  historicalMatches: (slug: string, season: string) =>
    apiRequest<ApiData[]>(endpoints.public.competitionHistoryMatches(slug, season)),
  historicalBracket: (slug: string, season: string) =>
    apiRequest<ApiData>(endpoints.public.competitionHistoryBracket(slug, season)),
  streams: (params?: ListParams) =>
    apiPaginated<Stream>(endpoints.public.streams, { query: params }),
  stream: (id: string) => apiRequest<Stream>(endpoints.public.stream(id)),
  articles: (params?: ListParams) =>
    apiPaginated<Article>(endpoints.public.articles, { query: params }),
  article: (slug: string) => apiRequest<Article>(endpoints.public.article(slug)),
  videos: (params?: ListParams) => apiPaginated<Video>(endpoints.public.videos, { query: params }),
  galleries: (params?: ListParams) =>
    apiPaginated<Gallery>(endpoints.public.galleries, { query: params }),
  gallery: (id: string) => apiRequest<Gallery>(endpoints.public.gallery(id)),
  sponsors: () => apiRequest<Sponsor[]>(endpoints.public.sponsors),
  captchaChallenge: () =>
    apiRequest<{ token: string; question: string; expiresAt: string }>(
      endpoints.public.captchaChallenge,
    ),
  submitForm: (formId: string, body: Record<string, unknown>) =>
    apiRequest<{ ok: true }>(endpoints.public.formSubmit(formId), { method: "POST", body }),
  subscribeNewsletter: (email: string) =>
    apiRequest<{ publicId: string }>(endpoints.public.newsletterSubscribe, {
      method: "POST",
      body: { email },
    }),
};
